/**
 * Pure helpers over the artifact's optional `demand` block (docs/archive/specs/demand-adjudicator.md,
 * "Viewer"). No fetching, no React — the demand components stay thin renderers over these, and the
 * doctrine that matters (what counts as a gap, what `no_signal` is allowed to claim) is testable
 * without a DOM.
 *
 * Everything here tolerates a missing block and missing/odd fields: an artifact built before the
 * demand pass existed has no `demand` key at all, and the panel is written by a model-assisted
 * stage whose axis names and anchors are not guaranteed to line up with the matrix.
 */
import type {
  Artifact,
  ArtifactDemand,
  ArtifactFeature,
  ArtifactVendor,
  AxisDemand,
  CarriedStance,
  ComparisonMatrix,
  DemandAssignmentCoverage,
  DemandJoin,
  DemandLaneCoverage,
  DemandProductStance,
  DemandQuote,
  DemandUnassignedGroup,
  DemandVendorFriction,
  DemandVerdict,
  Stance,
} from '../types.ts';
import { resolveCellEntry } from './artifact.ts';

/** How many quotes (or unassigned groups) a list shows before its "show all" toggle. */
export const QUOTE_DISPLAY_CAP = 5;
export const UNASSIGNED_GROUP_CAP = 12;

/**
 * The axis's demand entry, or null when the run gathered no demand OR the panel has no entry
 * under this exact axis name. Callers must keep those two apart from `no_signal`: null means
 * "we hold nothing to show", `no_signal` means "we looked and found nothing".
 */
export function axisDemandFor(artifact: Pick<Artifact, 'demand'>, axis: string): AxisDemand | null {
  return artifact.demand?.axes?.find((a) => a.axis === axis) ?? null;
}

/**
 * A gap axis is absent-dominant: among this axis's cells whose verdict is not `unknown`, `absent`
 * is a strict majority, and there is at least one such cell (design-system.md, "Demand
 * annotations"). `unknown` cells are excluded rather than counted against the gap — unmeasured
 * ground is not evidence either way, and a degraded run's all-`unknown` column must not dilute a
 * real gap. An axis with nothing but `unknown` is not a gap: no measured cell, no finding.
 *
 * Computed over the whole matrix, never the filtered view — hiding a vendor column must not turn
 * a row into a headline finding.
 */
export function isGapAxis(matrix: Pick<ComparisonMatrix, 'cells'>, axis: string): boolean {
  let measured = 0;
  let absent = 0;
  for (const cell of matrix.cells) {
    if (cell.axis !== axis || cell.verdict === 'unknown') continue;
    measured++;
    if (cell.verdict === 'absent') absent++;
  }
  return measured > 0 && absent * 2 > measured;
}

/** The hot colour is earned only by `wanted` on a gap row — the product's headline finding. */
export function isHeadlineDemand(verdict: DemandVerdict, gap: boolean): boolean {
  return verdict === 'wanted' && gap;
}

export const DEMAND_VERDICT_LABEL: Record<DemandVerdict, string> = {
  wanted: 'wanted',
  disputed: 'disputed',
  rejected: 'rejected',
  no_signal: 'no signal',
};

export function voicesLabel(voices: number): string {
  return `${voices} ${voices === 1 ? 'voice' : 'voices'}`;
}

// --- quotes ---

function timeOf(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
}

/**
 * Newest first — recency is the reader's first credibility question about a complaint. Quotes
 * with no parseable date sink to the end rather than sorting as 1970 or as "now". Stable, and
 * never mutates: the artifact's own order is the panel builder's, and other surfaces share it.
 */
export function orderQuotes(quotes: DemandQuote[]): DemandQuote[] {
  return quotes
    .map((quote, index) => ({ quote, index, time: timeOf(quote.published_at) }))
    .sort((a, b) => {
      if (a.time === null && b.time === null) return a.index - b.index;
      if (a.time === null) return 1;
      if (b.time === null) return -1;
      return b.time - a.time || a.index - b.index;
    })
    .map((x) => x.quote);
}

export interface Capped<T> {
  shown: T[];
  /** How many are held back behind the toggle; 0 means there is no toggle to draw. */
  hidden: number;
}

/** Display cap only — the data stays whole, and `showAll` always reaches every item. */
export function capList<T>(items: T[], showAll: boolean, cap: number = QUOTE_DISPLAY_CAP): Capped<T> {
  if (showAll || items.length <= cap) return { shown: items, hidden: 0 };
  return { shown: items.slice(0, cap), hidden: items.length - cap };
}

/**
 * Dates render as the ISO day: mono, verifiable, and the same string in every reader's timezone
 * (a localised date would disagree with the permalink's own timestamp across a dateline).
 */
export function formatDemandDate(iso: string | null | undefined): string | null {
  const t = timeOf(iso);
  return t === null ? null : new Date(t).toISOString().slice(0, 10);
}

/** "2024-03-21 → 2026-06-01", a single day when both ends agree, null when nothing was dated. */
export function formatDateRange(range: { earliest: string | null; latest: string | null } | null | undefined): string | null {
  const earliest = formatDemandDate(range?.earliest);
  const latest = formatDemandDate(range?.latest);
  if (!earliest && !latest) return null;
  if (!earliest || !latest || earliest === latest) return earliest ?? latest;
  return `${earliest} → ${latest}`;
}

/** One join, as the `.src` line names it: an axis by name, a product by name, a feature as
 * product › feature (› subfeature). The company is left off — the quote sits under its vendor. */
export function joinLabel(join: DemandJoin): string {
  switch (join.kind) {
    case 'axis':
      return join.axis;
    case 'product':
      return join.product;
    case 'feature':
      return [join.product, join.parent, join.entry].filter((s): s is string => !!s).join(' › ');
  }
}

/**
 * The homes a quote states beside its provenance: every join when it has more than one, nothing
 * when it has one (the list it sits in says that) or none recorded (a panel written before joins
 * existed). Design-system.md, "Joins on a quote".
 */
export function quoteJoinLabels(quote: Pick<DemandQuote, 'joins'>): string[] {
  const joins = quote.joins ?? [];
  return joins.length > 1 ? joins.map(joinLabel) : [];
}

const STANCE_ORDER: Stance[] = ['pain', 'wish', 'workaround', 'switching', 'rejection', 'praise'];

/**
 * Non-zero stance counts, demand stances first, then rejection, then praise (which counts toward
 * neither verdict — it corroborates supply, so it reads last). Counts are of quotes, not voices.
 * `general` is not a stance here: the axis head states it apart, as voices (`generalLabel`).
 */
export function stanceCounts(stances: Partial<Record<CarriedStance, number>> | null | undefined): { stance: Stance; count: number }[] {
  return STANCE_ORDER.map((stance) => ({ stance, count: stances?.[stance] ?? 0 })).filter((s) => s.count > 0);
}

/**
 * "N general" for an axis whose panel counted general voices (design-system.md, "General voices
 * on an axis"); null when there are none or the panel predates the count, so nothing is drawn.
 */
export function generalLabel(entry: Pick<AxisDemand, 'general_voices'>): string | null {
  const n = entry.general_voices ?? 0;
  return n > 0 ? `${n} general` : null;
}

/** The explanation a "N general" count carries in its `title`. */
export const GENERAL_VOICES_TITLE =
  'People who want this in general, counted toward the verdict. Their quotes are about one product’s feature, so they are listed under that product, not here.';

// --- coverage ---

export interface CoverageSummary {
  lanes: number;
  searches: number;
  threads_fetched: number;
  threads_kept: number;
  date_range_seen: { earliest: string | null; latest: string | null };
}

/** Totals across lanes: the denominator every `no_signal` is read against. */
export function summariseCoverage(lanes: DemandLaneCoverage[] | null | undefined): CoverageSummary {
  const summary: CoverageSummary = {
    lanes: 0,
    searches: 0,
    threads_fetched: 0,
    threads_kept: 0,
    date_range_seen: { earliest: null, latest: null },
  };
  let earliest: number | null = null;
  let latest: number | null = null;
  for (const lane of lanes ?? []) {
    summary.lanes++;
    summary.searches += lane.searches?.length ?? 0;
    summary.threads_fetched += lane.threads_fetched ?? 0;
    summary.threads_kept += lane.threads_kept ?? 0;
    const e = timeOf(lane.date_range_seen?.earliest);
    const l = timeOf(lane.date_range_seen?.latest);
    if (e !== null && (earliest === null || e < earliest)) {
      earliest = e;
      summary.date_range_seen.earliest = lane.date_range_seen.earliest;
    }
    if (l !== null && (latest === null || l > latest)) {
      latest = l;
      summary.date_range_seen.latest = lane.date_range_seen.latest;
    }
  }
  return summary;
}

/** The demand pass searches one lane per matrix axis, named `capability:<axis>`. */
export function axisLane(demand: ArtifactDemand, axis: string): DemandLaneCoverage | null {
  return demand.coverage?.find((l) => l.lane === `capability:${axis}`) ?? null;
}

export interface AxisCoverage {
  /** `lane`: this capability's own searches. `run`: no such lane, so the whole run's totals. */
  scope: 'lane' | 'run';
  summary: CoverageSummary;
  searches: DemandLaneCoverage['searches'];
}

/**
 * What stands behind one axis's verdict. Falls back to the run-wide totals when the axis has no
 * lane of its own, and says so via `scope` — a `no_signal` must always be shown against SOME
 * stated denominator, but never against one that claims to be more specific than it is.
 */
export function axisCoverage(demand: ArtifactDemand, axis: string): AxisCoverage {
  const lane = axisLane(demand, axis);
  if (lane) return { scope: 'lane', summary: summariseCoverage([lane]), searches: lane.searches ?? [] };
  return { scope: 'run', summary: summariseCoverage(demand.coverage), searches: [] };
}

/** "capability:Web clipper" -> kind + name, for the where-we-looked table. */
export function splitLane(lane: string): { kind: string; name: string } {
  const i = lane.indexOf(':');
  if (i === -1) return { kind: 'lane', name: lane };
  return { kind: lane.slice(0, i), name: lane.slice(i + 1) };
}

// --- vendor friction ---

/** Same identity the vendor pivot keys features by: a name alone is ambiguous across products. */
export function featureKey(feature: Pick<ArtifactFeature, 'product' | 'name'>): string {
  return `${feature.product}::${feature.name}`;
}

export interface AnchoredFriction {
  friction: DemandVendorFriction;
  /** Set when the friction anchors to a subfeature of the keyed feature. */
  subfeature: string | null;
}

export interface VendorFrictionIndex {
  /** Keyed by `featureKey` of the feature the friction resolved to. */
  anchored: Map<string, AnchoredFriction[]>;
  /**
   * Product-level friction (`entry: null`), plus any anchored entry that did not resolve against
   * this vendor's features. An unresolved anchor is kept here under its own name rather than
   * dropped: a complaint we cannot place is still a complaint we found.
   */
  vendorLevel: DemandVendorFriction[];
}

/**
 * Friction for one vendor, matched by `company` and anchored with `resolveCellEntry` — the one
 * (product, entry, parent) convention the viewer has, so a cell citation and a friction anchor
 * can never disagree about which feature a name means. Friction carries no `kind`: a `parent`
 * says subfeature; otherwise the name is tried as a feature first, then as a subfeature.
 */
export function vendorFriction(demand: ArtifactDemand | null | undefined, vendor: ArtifactVendor): VendorFrictionIndex {
  const index: VendorFrictionIndex = { anchored: new Map(), vendorLevel: [] };
  for (const friction of demand?.vendor_friction ?? []) {
    if (friction.company !== vendor.company) continue;
    if (!friction.entry) {
      index.vendorLevel.push(friction);
      continue;
    }
    const anchor = { entry: friction.entry, parent: friction.parent ?? null, product: friction.product ?? null };
    const resolved =
      (anchor.parent ? null : resolveCellEntry(vendor, { ...anchor, kind: 'feature' })) ??
      resolveCellEntry(vendor, { ...anchor, kind: 'subfeature' });
    if (!resolved) {
      index.vendorLevel.push(friction);
      continue;
    }
    const key = featureKey(resolved.feature);
    const list = index.anchored.get(key) ?? [];
    list.push({ friction, subfeature: resolved.subfeature?.name ?? null });
    index.anchored.set(key, list);
  }
  return index;
}

/** This vendor's product-level stance rows, in the panel's order. Empty for a panel written
 * before the rows existed: its product-level friction is in `vendorFriction().vendorLevel`. */
export function productStances(demand: ArtifactDemand | null | undefined, vendor: ArtifactVendor): DemandProductStance[] {
  return (demand?.product_stances ?? []).filter((s) => s.company === vendor.company);
}

/** Quotes across a set of friction entries — what the row's count tag reports. */
export function frictionQuoteCount(entries: { friction: DemandVendorFriction }[]): number {
  return entries.reduce((n, e) => n + (e.friction.quotes?.length ?? 0), 0);
}

// --- assignment coverage ---

/**
 * How much of the demand corpus the assignment pass actually read, from the panel's own
 * `assignment` counts (written by the panel builder from the assignments). Null for a panel
 * written before those existed: such a panel folded never-read signals into the pile, so no read
 * count can be claimed for it — and the viewer must not invent one from the quotes it renders.
 */
export function assignmentTotals(demand: ArtifactDemand | null | undefined): DemandAssignmentCoverage | null {
  const a = demand?.assignment;
  if (!a || typeof a.signals !== 'number' || typeof a.read !== 'number' || typeof a.unread !== 'number') return null;
  return a;
}

// --- unassigned ---

/**
 * Most voices first, stable otherwise: the pile is long in real runs (hundreds of groups, most
 * with one voice), so the groups several people independently raised must not be buried.
 */
export function orderUnassigned(groups: DemandUnassignedGroup[] | null | undefined): DemandUnassignedGroup[] {
  return (groups ?? [])
    .map((group, index) => ({ group, index }))
    .sort((a, b) => (b.group.voices ?? 0) - (a.group.voices ?? 0) || a.index - b.index)
    .map((x) => x.group);
}
